import type { Brand, VehicleModel, VehicleTrim } from 'car-insurance/types';

type MapperFunctionOutput = { label: string; value: string }[];

type MapperFunctionInputParams = {
  models: VehicleModel[];
  brand: Brand;
  useBolttechData?: boolean;
};

const upperCasedMakes = [
  'BMW',
  'KIA',
  'MG',
  'BYD',
  'SAAB',
  'HSV',
  'M.G.',
  'TVR',
  'DFSK',
];

export function capitalize(input?: string | null): string {
  if (!input) return '';
  return input
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

const preTransformedMakes = [
  { sourceValue: 'A.M.GENERAL', actualvalue: 'A.M.General' },
  { sourceValue: 'MCLAREN', actualvalue: 'McLaren' },
  { sourceValue: 'MERCEDES-BENZ', actualvalue: 'Mercedes-Benz' },
];

const mapVehicleModelsSS = (
  vehicleModels: VehicleModel[],
  useBolttechData?: boolean,
): MapperFunctionOutput => {
  if (useBolttechData) {
    return (vehicleModels || []).map((model) => ({
      label: model.modelName,
      value: model.modelId,
    }));
  }

  return (vehicleModels || []).map((model) => ({
    label: `${model.modelName} ${model.trim}`,
    value: model.modelId,
  }));
};

const mapVehicleModelsMH = (
  vehicleModels: VehicleModel[],
): MapperFunctionOutput => {
  const mappedVehicleModels = vehicleModels.map((model) => ({
    label: capitalize(model.modelName),
    value: model.modelId,
  }));

  const set = new Set();

  return mappedVehicleModels.filter((item) => {
    const duplicate = set.has(item.label);
    set.add(item.label);
    return !duplicate;
  });
};

const transformMakeName = (make: string) => {
  // if (!make) {
  //   return '';
  // }
  if (typeof make !== 'string' || !make) {
    return '';
  }

  if (upperCasedMakes.includes(make)) {
    const index = upperCasedMakes.indexOf(make.toUpperCase());
    return upperCasedMakes[index];
  }

  const preTransformedMake = preTransformedMakes.find(
    (item) => item.sourceValue === make,
  );
  if (preTransformedMake) {
    return preTransformedMake.actualvalue;
  }

  // if (Array.isArray(make)) return make?.split(' ').map(capitalize).join(' ');
  return make.split(' ').map(capitalize).join(' ');
};

export const mapVehicleModels = (
  params: MapperFunctionInputParams,
): MapperFunctionOutput => {
  const { brand, models, useBolttechData = false } = params;
  if (brand === 'moneymax') {
    return (models || []).map((model) => ({
      label: model.modelName,
      value: model.modelId,
    }));
  }
  if (brand === 'singsaver') {
    return mapVehicleModelsSS(models as VehicleModel[], useBolttechData);
  }

  return mapVehicleModelsMH(models as VehicleModel[]);
};

export const mapVehicleMakers = (
  models: string[] | { id: string; name: string }[],
  brand: Brand,
): MapperFunctionOutput => {
  if (brand === 'singsaver') {
    return ((models as { id: string; name: string }[]) || []).map((make) => ({
      label: transformMakeName(make?.name) ?? '',
      value: make?.id ?? '',
    }));
  }

  const transformedMakeNames = (models as string[]).map((model) =>
    transformMakeName(model),
  );

  return transformedMakeNames?.map((make) => ({
    label: make ?? '',
    value: brand === 'moneymax' ? (make ?? '') : (make?.toUpperCase() ?? ''),
  }));
};

export const mapVehicleTrims = (
  models: VehicleTrim[],
): MapperFunctionOutput => {
  return models.map((variant) => ({
    label: variant.trim,
    value: variant.modelId || variant.trim,
  }));
};
